import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useIsGlobetrotter } from '@bwoty-web/ui-kit/hooks';
import { useEffect, useMemo } from 'react';
import { BackLink, Breadcrumbs } from '@bwoty-web/ui-kit';

import FadeIn from '../utils/fadeIn';
import ErrorPopup from '../components/shared/errorPopup/ErrorPopup';
import RoomCardList from '../components/priceMatrix/RoomCardList';
import MobileHeader from '../components/priceMatrix/MobileHeader';
import PriceDetails from '../components/shared/priceDetails/PriceDetails';
import PriceSummary from '../components/shared/priceSummary/PriceSummary';
import LoaderHeading from '../components/shared/loaders/LoaderHeading';
import GlobalContext from '../context/globalContext';
import FlightInfo from '../components/hotelList/flightInfo/FlightInfo';
import PriceMatrixTable from '../components/priceMatrix/PriceMatrixTable';
import MessageContainer from '../components/shared/message/MessageContainer';
import HeadingContainer from '../components/shared/headingContainer/HeadingContainer';
import PriceMatrixLoader from '../components/priceMatrix/loaders/PriceMatrixLoader';
import AlternativeHotels from '../components/priceMatrix/AlternativeHotels';
import { texts } from '../assets/texts';
import { urlUtils } from '../utils/url';

import { addToCart, errorPopupToggled, getFlightTime, init } from '../redux/actions/priceMatrixActions';
import { roomGroupSelected } from '../redux/reducers/priceMatrix/hotelData';
import {
  selectAlternativeHotels,
  selectAlternativeHotelsLabels,
  selectAppStatus,
  selectError,
  selectHotelData,
  selectLabels,
  selectMessage,
  selectSearchInformation,
  selectSiteSettings,
  selectSummaryReturnLink,
  selectTransportData,
  selectUserId,
} from '../redux/selectors/priceMatrix';

import './priceMatrixContainer.scss';

// No props, one field per `useSelector`.
const PriceMatrixContainer = () => {
  const dispatch = useDispatch();
  const alternativeHotels = useSelector(selectAlternativeHotels);
  const alternativeHotelsLabels = useSelector(selectAlternativeHotelsLabels);
  const appStatus = useSelector(selectAppStatus);
  const error = useSelector(selectError);
  const hotelData = useSelector(selectHotelData);
  const labels = useSelector(selectLabels);
  const message = useSelector(selectMessage);
  const searchInformation = useSelector(selectSearchInformation);
  const siteSettings = useSelector(selectSiteSettings);
  const summaryReturnLink = useSelector(selectSummaryReturnLink);
  const transportData = useSelector(selectTransportData);
  const userId = useSelector(selectUserId);
  const isGlobetrotter = useIsGlobetrotter();
  useEffect(() => {
    dispatch(init());
  }, [dispatch]);

  const globals = useMemo(
    () => ({
      labels,
      siteSettings,
    }),
    [siteSettings, labels],
  );

  const showLoader = !labels || !appStatus.packageDataInitialized;
  const isCombination = urlUtils.isCombination();
  const isHotelOnly = urlUtils.getTypeFromUrl() === 'IndependentHotelOnly';

  if (showLoader) {
    if (isCombination) {
      return (
        <LoaderHeading
          heading={texts[siteSettings.siteId].loaderHeadingShort}
          topMargin
        />
      );
    }

    return (
      <div>
        <PriceMatrixLoader
          isGlobetrotter={isGlobetrotter}
          isHotelOnly={isHotelOnly}
        />
      </div>
    );
  }

  const showPriceMatrix = !!hotelData?.selectedRoomGroup && (isHotelOnly || !!transportData);

  const showAlternativeHotels = (!hotelData?.roomGroups || hotelData?.noPrices) && alternativeHotels?.length > 0;

  const { selectedRoomGroup } = hotelData ?? {};

  const clickBook = () => {
    if (selectedRoomGroup.isReadyForPassengerDetails) {
      dispatch(addToCart());
    } else {
      window.location = urlUtils.standaloneUrlHandler(`/${selectedRoomGroup.link}`);
    }
  };

  const renderPriceDetails = () => {
    return (
      <PriceDetails
        selectedRoomGroup={selectedRoomGroup}
        hotelInformation={{
          ...hotelData,
          roomDescription: hotelData.selectedRoomGroup.rooms,
          amenitiesDescription: hotelData.selectedRoomGroup?.amenities?.map((amenity) => amenity?.name)?.join(', '),
        }}
        priceBreakDown={selectedRoomGroup.priceBreakDowns}
        searchInformation={searchInformation}
        labels={labels.priceDetails}
        outBoundInformation={transportData?.outBoundFlights?.[0]}
        isSummaryView
      />
    );
  };

  const priceSummaryLabels = {
    button: isCombination ? labels.roomSelectBtnLabel : labels.nextStepAndBook,
    totalPrice: labels.totalPrice,
    priceDetails: labels.priceDetailsLinkLabel,
    couponInfo: labels.couponInfo,
  };

  const productAndPaxInfo = `${labels.productsIncludedParsed}, ${searchInformation?.passengerInfo}`;

  return (
    <GlobalContext.Provider value={globals}>
      <FadeIn>
        {!isHotelOnly && !isCombination && <FlightInfo inPriceMatrix />}
        <div className={cx({ 'iw-price-matrix--hotel-only': isHotelOnly })}>
          <MessageContainer
            isHotelOnly={isHotelOnly}
            labels={labels}
            message={message}
            style={{ marginBottom: 20 }}
            user={userId}
          />
          {isCombination &&
            (isGlobetrotter ? (
              <>
                <Breadcrumbs
                  className="iw-price-matrix__breadcrumbs"
                  links={[
                    {
                      name: labels.iwGeneral.breadcrumbComboSummaryLinkLabel,
                      uri: urlUtils.standaloneUrlHandler(summaryReturnLink),
                      target: null,
                    },
                    {
                      name: labels.combinationHeader,
                      uri: window.location.href,
                      target: null,
                    },
                  ]}
                />
                <h1 className="iw-price-matrix__combination-heading">{labels.combinationHeader}</h1>
                <div className="iw-price-matrix__heading-divider" />
              </>
            ) : (
              <HeadingContainer>
                <h1 className="iw-price-matrix__combination-heading">{labels.combinationHeader}</h1>
                <BackLink href={urlUtils.standaloneUrlHandler(summaryReturnLink)}>
                  {labels.iwGeneral.backToSummary}
                </BackLink>
              </HeadingContainer>
            ))}
          {isHotelOnly && (
            <h3 className="iw-price-matrix-hotel-only--dates-heading">
              {`${hotelData?.checkIn} - ${hotelData?.checkOut}`}
            </h3>
          )}
          {hotelData?.roomGroups?.length > 0 &&
            (isGlobetrotter || isHotelOnly ? (
              <RoomCardList
                hotel={hotelData}
                isHotelOnly={isHotelOnly}
                labels={labels}
                searchInformation={searchInformation}
                selectRoomGroup={(roomGroup) => dispatch(roomGroupSelected(roomGroup))}
                showRoomTypeLink={siteSettings?.showRoomTypeLink}
              />
            ) : (
              <>
                {!isCombination && (
                  <MobileHeader
                    labels={labels}
                    transport={transportData}
                    getFlightTime={() => dispatch(getFlightTime())}
                  />
                )}
                <PriceMatrixTable
                  getFlightTime={() => dispatch(getFlightTime())}
                  hotel={hotelData}
                  labels={labels}
                  selectRoomGroup={(roomGroup) => dispatch(roomGroupSelected(roomGroup))}
                  showRoomTypeLink={siteSettings?.showRoomTypeLink}
                  transport={transportData}
                />
              </>
            ))}
          {showPriceMatrix && (
            <PriceSummary
              labels={priceSummaryLabels}
              productAndPaxInfo={productAndPaxInfo}
              totalPrice={hotelData?.selectedRoomGroup?.priceBreakDowns?.totalPrice}
              onNavigate={() => clickBook()}
              priceDetails={renderPriceDetails()}
              disclaimer={hotelData.selectedRoomGroup.hasCharges ? labels.paymentText : null}
              loading={hotelData.loading}
              discounted={selectedRoomGroup?.priceBreakDowns?.isDiscounted}
            />
          )}
          {showAlternativeHotels && (
            <AlternativeHotels
              hotels={alternativeHotels}
              alternativeHotelsLabels={alternativeHotelsLabels}
              transportData={transportData}
              searchInformation={searchInformation}
              labels={labels}
              siteSettings={siteSettings}
            />
          )}
          {error.visible && (
            <ErrorPopup
              closePopup={() => dispatch(errorPopupToggled({ visible: false }))}
              labels={labels.errors}
              error={error}
            />
          )}
        </div>
      </FadeIn>
    </GlobalContext.Provider>
  );
};

export default PriceMatrixContainer;
